// T040: Create title generation utility

/**
 * UTF-8 바이트 기준으로 문자열을 자릅니다.
 * 문자 경계를 존중하여 멀티바이트 문자가 깨지지 않습니다.
 *
 * @param str 원본 문자열
 * @param maxBytes 최대 바이트 수
 * @returns 잘린 문자열
 */
export function truncateToByteLength(str: string, maxBytes: number): string {
  const encoder = new TextEncoder()
  const encoded = encoder.encode(str)

  if (encoded.length <= maxBytes) {
    return str
  }

  // 바이트 단위로 자르고 유효한 UTF-8로 디코딩 시도
  let length = maxBytes
  while (length > 0) {
    const truncated = encoded.slice(0, length)
    try {
      const decoder = new TextDecoder('utf-8', { fatal: true })
      return decoder.decode(truncated)
    } catch {
      // 잘못된 UTF-8 시퀀스면 한 바이트씩 줄임
      length--
    }
  }

  return ''
}

/**
 * 본문에서 제목을 자동 생성합니다.
 * 마크다운 포맷을 제거하고 지정된 바이트 수로 잘라냅니다.
 *
 * @param body 본문 내용
 * @param maxBytes 최대 바이트 수 (기본값: 256)
 * @returns 생성된 제목
 */
export function generateTitleFromBody(body: string, maxBytes: number = 256): string {
  // 마크다운 포맷 제거
  const stripped = body
    .replace(/[#*`>\n]/g, '') // 마크다운 기호 및 줄바꿈 제거
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // 링크를 텍스트로 변환
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '') // 이미지 제거
    .trim()

  if (!stripped) {
    return 'Untitled Post'
  }

  return truncateToByteLength(stripped, maxBytes)
}

/**
 * Generates a title from post body (auto-title feature)
 * Takes first 50 characters after removing markdown
 * @deprecated Use generateTitleFromBody for byte-safe truncation
 */
export function generateTitle(body: string): string {
  // Remove markdown formatting
  const stripped = body
    .replace(/[#*`>\n]/g, '') // Remove markdown symbols and newlines
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Convert links to text
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '') // Remove images
    .trim()

  if (!stripped) {
    return 'Untitled Post'
  }

  // Take first 50 characters
  const title = stripped.length > 50 ? stripped.substring(0, 50) + '...' : stripped

  return title
}

/**
 * Extracts summary from post body for preview
 */
export function generateSummary(body: string, maxLength: number = 250): string {
  const stripped = body
    .replace(/[#*`>]/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '')
    .trim()

  if (stripped.length <= maxLength) {
    return stripped
  }

  return stripped.substring(0, maxLength) + '...'
}

/**
 * Calculates estimated read time in minutes
 */
export function calculateReadTime(body: string): number {
  const wordCount = body.trim().split(/\s+/).length
  const wordsPerMinute = 200
  return Math.ceil(wordCount / wordsPerMinute)
}
